import React, { useCallback, useContext, useMemo } from 'react'

import BaseTripItemSummaryCard from './BaseTripItemSummaryCard'
import { startPlaceOfTripItem } from '../../utils'

import TextButton from 'components/Luxkit/Button/TextButton'
import GeoContext from 'contexts/geoContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import BookmarkLocation from 'tripPlanner/components/Bookmark/BookmarkCardNew/BookmarkLocation'
import PlannedExperienceCtaContent from 'tripPlanner/components/ItemCard/Experience/PlannedExperienceCtaContent'
import { PlannedItemPriceContent } from 'tripPlanner/components/ItemCard/PlannedItemPriceContent'
import { BOOKING_DETAILS_COPY } from 'tripPlanner/config'
import TripContext from 'tripPlanner/contexts/TripContext'
import { useExperience } from 'tripPlanner/hooks/api'
import { usePlaceRating } from 'tripPlanner/hooks/Google/usePlaceRating'
import { ExperienceItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatDateTimeOrDateRange,
  isCustomItem,
  isLeBooking,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/restoreCart'
import { isExperienceAvailable } from 'tripPlanner/utils/experience'

interface Props {
  className?: string
  item: ExperienceItem
  alwaysShadow?: boolean
  showCloseButton?: boolean
  onClose?: () => void
}

const ExperienceItemSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  ({ className, item, alwaysShadow, showCloseButton, onClose }, ref) => {
    const { currentCurrency, currentRegionCode } = useContext(GeoContext)
    const tripContext = useContext(TripContext)
    const canEdit = tripContext?.permissions.canEdit ?? false
    const viewAsTemplate = tripContext?.viewAsTemplate
    const place = startPlaceOfTripItem(item)
    const { data: rating } = usePlaceRating(
      place?.id ?? '',
      place?.id !== undefined && isCustomItem(item),
    )
    const experienceId = item.code ?? ''

    const { data: experience } = useExperience({
      experienceId,
      currentCurrency,
      currentRegionCode,
    })
    const offerImageId = experience?.images?.[0]?.id
    const imageURL = experience?.images?.[0]?.url

    const offerLink = useMemo(() => {
      return isLeBookmark(item) ?
        buildProductLink(item, true, { experience }) :
        undefined
    }, [experience, item])

    const myEscapesLink =
      canEdit && !viewAsTemplate ? buildMyEscapesOrderLink(item) : undefined

    const navigateTo = useNavigateTo()

    const isOfferAvailable = experience ?
      isExperienceAvailable(experience, viewAsTemplate) :
      false

    const viewBooking = useCallback(() => {
      if (isLeBooking(item) && myEscapesLink) {
        navigateTo(myEscapesLink)
      }
    }, [item, myEscapesLink, navigateTo])

    const priceAreaContent = useMemo(() => {
      if (!isLeBookmark(item)) return null
      const { price, discountPercent, currencyCode } = item.savedItemData
      return (
        <PlannedItemPriceContent
          discountPercent={discountPercent}
          price={price}
          currencyCode={currencyCode}
          isOfferAvailable={isOfferAvailable}
        />
      )
    }, [isOfferAvailable, item])

    const ctaAreaContent = useMemo(() => {
      if (isLeBookmark(item) && offerLink) {
        return (
          <PlannedExperienceCtaContent
            item={item}
            isOfferAvailable={isOfferAvailable}
            variant="mapSummary"
            offerLink={offerLink}
          />
        )
      } else if (isLeBooking(item)) {
        return (
          <>
            <div />
            <TextButton
              kind="secondary"
              size="small"
              variant="default"
              nonInteractable
              onClick={viewBooking}
            >
              {BOOKING_DETAILS_COPY}
            </TextButton>
          </>
        )
      }
    }, [isOfferAvailable, item, viewBooking, offerLink])

    const locationDescriptionContent = useMemo(() => {
      return (
        <BookmarkLocation
          variant="with_experience"
          startLocation={experience?.location.name}
          typeOfExperience={experience?.primaryCategory?.name}
          showIcon={false}
        />
      )
    }, [experience])

    const timeFormat = useRegionTimeFormat()
    const timeDescription = formatDateTimeOrDateRange(item, timeFormat)

    if (!place) {
      return null
    }

    return (
      <BaseTripItemSummaryCard
        ref={ref}
        className={className}
        item={item}
        alwaysShadow={alwaysShadow}
        showCloseButton={showCloseButton}
        onClose={onClose}
        offerImageId={offerImageId}
        imageURL={imageURL}
        offerLink={offerLink}
        rating={rating}
        place={place}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        locationDescriptionContent={locationDescriptionContent}
        descriptors={[timeDescription]}
      />
    )
  },
)

export default ExperienceItemSummaryCard
